import {
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { from, lastValueFrom, Observable, switchMap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from './auth.service';
import { environment } from '../../environments';

interface JwtPayload {
  roles?: string[];
}

function buildBaseHeaders(fileUpload: boolean): HttpHeaders {
  if (fileUpload) {
    return new HttpHeaders()
      .set('Access-Control-Allow-Origin', environment.apiUrl)
      .set('Access-Control-Allow-Methods', 'GET')
      .set('Access-Control-Allow-Headers', 'Content-Type')
      .set('Access-Control-Max-Age', '86400');
  }
  return new HttpHeaders()
    .set('Accept', ['application/json', 'image/svg+xml'])
    .set('Content-Type', ['application/json', 'image/svg+xml'])
    .set('Access-Control-Allow-Origin', environment.apiUrl)
    .set('Access-Control-Allow-Methods', 'GET')
    .set('Access-Control-Allow-Headers', 'Content-Type')
    .set('Access-Control-Max-Age', '86400');
}

function mergeHeaders(base: HttpHeaders, incoming?: HttpHeaders): HttpHeaders {
  if (!incoming) return base;
  let merged = base;
  incoming.keys().forEach((key) => {
    const val = incoming.get(key);
    if (val) merged = merged.append(key, val);
  });
  return merged;
}

function saveRoles(token: string, authService: AuthService): void {
  const decoded = jwtDecode<JwtPayload>(token);
  if (Array.isArray(decoded.roles)) {
    authService.setRoles(decoded.roles);
  }
}

async function buildAuthorizedHeaders(
  cal: CalAngularService,
  authService: AuthService,
  incoming: HttpHeaders,
  isFileUpload: boolean
): Promise<HttpHeaders> {
  const base = buildBaseHeaders(isFileUpload);
  const merged = mergeHeaders(base, incoming);
  const token = await lastValueFrom(cal.getAADToken([environment.scopeUrl], false));
  saveRoles(token as string, authService);
  return merged.set('Authorization', `Bearer ${token}`);
}

/**
 * Re-enters Angular's zone so downstream operators (and the HTTP
 * request/response) run inside NgZone and trigger change detection.
 *
 * This is required because `cal.getAADToken()` may resolve its
 * internal Promise outside Angular's zone (e.g. MSAL iframe flow),
 * which would otherwise leave every subsequent subscriber callback
 * outside the zone – preventing automatic change-detection.
 */
function enterNgZone<T>(ngZone: NgZone) {
  return (source: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const sub = source.subscribe({
        next: (value) => ngZone.run(() => subscriber.next(value)),
        error: (err) => ngZone.run(() => subscriber.error(err)),
        complete: () => ngZone.run(() => subscriber.complete()),
      });
      return () => sub.unsubscribe();
    });
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const cal = inject(CalAngularService);
  const authService = inject(AuthService);
  const ngZone = inject(NgZone);
  const isFileUpload = req.url.includes('UploadFileAttachment');

  return from(buildAuthorizedHeaders(cal, authService, req.headers, isFileUpload)).pipe(
    enterNgZone(ngZone),
    switchMap((headers) => next(req.clone({ headers })))
  );
};
